<template>
  <div class="status-bar" data-testid="status-bar">
    <div class="status-bar__wrapper">
      <div class="status-bar__row">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="status-bar__row__wrapper"
        >
          <div
            class="status-bar__indicator"
            :class="{
              'status-bar__indicator__first': isFirstStep(index),
              'status-bar__indicator__current': isCurrentStep(index),
              'status-bar__indicator__last': isLastStep(index)
            }"
          />
        </div>
      </div>
      <div class="status-bar__row">
        <div
          v-for="(step, index) in steps.length - 1"
          :key="index"
          class="status-bar__row__background"
          :style="isDone(index) ? computedStatusStyle : ''"
        />
      </div>
    </div>
    <div class="status-bar__description">
      <div
        v-for="(step, index) in steps"
        :key="index"
        :class="{
          'status-bar__description__current': isCurrentStep(index),
          'status-bar__description__middle': isStepMiddle(index),
          'status-bar__description_removed': isStepToRemove(index)
        }"
      >
        <p class="text-p-3">
          {{ step.title }}
        </p>
        <p class="text-p-5">
          {{ step.description }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

interface Step {
  title: string;
  description: string;
}

@Component({
  name: 'StatusBar'
})
export default class StatusBar extends Vue {
  private NebraskaColors = NebraskaColors;

  @Prop({ type: Array })
  private steps!: Step[];

  @Prop({ type: Number, default: 1 })
  private currentStep!: number;

  @Prop({ type: Number })
  private stepToRemove?: number;

  @Prop({ type: String, default: NebraskaColors.progressPositive })
  private progressColor?: NebraskaColors;

  get computedStatusStyle() {
    return { backgroundColor: this.progressColor, zIndex: 10 };
  }

  private isDone(index: number) {
    const parseStep = this.currentStep - 1;
    if (parseStep === Number(this.steps[this.steps.length + 1])) {
      return true;
    } else if (parseStep > index) {
      return true;
    }
  }

  private isFirstStep(index: number) {
    return index === 0;
  }

  private isCurrentStep(index: number) {
    return index + 1 === this.currentStep;
  }

  private isLastStep(index: number) {
    return this.steps.length === index + 1;
  }

  private isStepMiddle(index: number) {
    return index === 1;
  }

  private isStepToRemove(index: number) {
    return index + 1 === this.stepToRemove;
  }
}
</script>

<style lang="less" scoped>
.status-bar {
  display: flex;
}

.status-bar__wrapper {
  width: 20px;
  flex-shrink: 0;
  position: relative;
  z-index: 5;
}

.status-bar__row {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  > div {
    flex: 1;
    display: flex;
    justify-content: space-between;
  }
  &:first-of-type {
    position: absolute;
  }
}

.status-bar__row__wrapper {
  width: 100%;
  flex-direction: column;
  &:nth-child(2) {
    justify-content: center;
  }
  &:last-of-type {
    justify-content: flex-end;
  }
}

.status-bar__indicator {
  width: 20px;
  height: 20px;
  border-radius: @size-radius-max;
  background-color: @background-tertiary;
  border: @size-border-x500 solid @neutral-primary;
  box-sizing: border-box;
  &.status-bar__indicator__first {
    justify-content: flex-start;
  }
  &.status-bar__indicator__current {
    background-color: @neutral-primary;
    z-index: 15;
    justify-content: center;
    border: @size-border-x500 solid @neutral-primary;
    box-sizing: border-box;
  }
  &.status-bar__indicator__last {
    justify-content: flex-end;
  }
}

.status-bar__row__background {
  background: @background-tertiary;
  width: 100%;
  border-radius: @size-radius-x300;
  &:first-of-type {
    border-radius: @size-radius-x300 @size-radius-x300 0px 0px;
  }
  &:last-of-type {
    border-radius: 0px 0px @size-radius-x300 @size-radius-x300;
  }
}

.status-bar__description {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: @text-secondary;
  padding-left: @size-spacing-x400;
  div {
    flex: 1;
  }
}

.status-bar__description__current {
  color: @text-primary;
  p:first-of-type {
    font-weight: bold;
  }
}

.status-bar__description__middle {
  padding: @size-spacing-x600 0;
}

.status-bar__description_removed {
  p {
    display: none;
  }
}
</style>
